import React, { useState } from "react";
import TextFields from "../../../components/formcomponents/TextField";
import TextArea from "../../../components/formcomponents/TextArea";
import { useDispatch } from "react-redux";
import { toast } from 'react-toastify';
import { addCollaboration } from "../../../redux/actions/action";
import { useNavigate } from "react-router-dom";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const AddCollaborationForm = ({ onClose, onSave }) => {
  const [formData, setFormData] = useState({
    projectNo: "",
    projectName: "",
    projectDescription: "",
    organizationName: "",
    uploadPhoto: null,
    category: "",
    researchArea: "",
    startDate: "",
    endDate: "",
    teamSize: "",
    teamMembers: "",
  });

  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    validateField(name, value);
  };

  // Validation
  const validateField = (name, value) => {
    let error = "";
    //const today = new Date();

    switch (name) {
      case "projectNo":
        if (!value.trim()) {
          error = "Project No is required.";
        }
        break;

      case "projectName":
        if (!/^[A-Za-z0-9\s]+$/.test(value)) {
          error = "Project Name must contain only alphanumeric characters.";
        }
        break;

      case "teamSize":
        if (!/^\d+$/.test(value) || value < 1) {
          error = "Team Size must be 1 or greater.";
        }
        break;

      case "teamMembers":
        if (value) {
          const membersArray = value.split(",").map((name) => name.trim());
          if (membersArray.length !== parseInt(formData.teamSize, 10)) {
            error = `Number of team members should match team size (${formData.teamSize}).`;
          }
        }
        break;

      case "startDate": {
        const minDate = new Date("2017-01-01"); // earliest allowed date
        const selectedDate = new Date(value);

        if (selectedDate < minDate) {
          error = "Start date cannot be before 2017.";
        }
        break;
      }

      case "endDate":
        if (new Date(value) <= new Date(formData.startDate)) {
          error = "End date must be greater than the start date.";
        }
        break;

      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    const validationErrors = {};
    Object.keys(formData).forEach((field) => {
      validateField(field, formData[field]);
      if (errors[field]) validationErrors[field] = errors[field];
    });

    if (Object.keys(validationErrors).length > 0) {
      console.error("Validation failed:", validationErrors);
      return;
    }

    try {
      await dispatch(addCollaboration(formData)); // <-- projectNo will be included
      onSave();
      onClose();
      navigate('/hrDashboard');
      toast.success("Successfully added the project details");
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  return (
    <div className="self-stretch flex flex-col items-start justify-start text-start text-xl text-black font-poppins rounded-3xl w-[750px]">
      <form onSubmit={(e) => e.preventDefault()} className="w-full bg-color p-6 rounded-xl shadow-lg">

        {/* Project No - Top Center */}
        <div className="grid grid-cols-1 mb-6">
          <div className="flex justify-center">
            <div className="w-1/2">
              <TextFields
                label="Project No*"
                name="projectNo"
                type="text"
                value={formData.projectNo}
                onChange={handleChange}
                placeholder="Enter Project Number"
                required
                className="w-full h-[45px]"
              />
              {errors.projectNo && <p className="text-red-500 text-sm">{errors.projectNo}</p>}
            </div>
          </div>
        </div>

        {/* Project Name + Sponsor */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <TextFields
            label="Project Name*"
            name="projectName"
            type="text"
            value={formData.projectName}
            onChange={handleChange}
            placeholder="Enter Project Name"
            required
            className="w-full h-[45px]"
          />
          {errors.projectName && <p className="text-red-500 text-sm">{errors.projectName}</p>}

          <TextFields
            label="Project Sponsor*"
            name="organizationName"
            type="text"
            value={formData.organizationName}
            onChange={handleChange}
            placeholder="Enter Organization Name"
            required
            className="w-full h-[45px]"
          />
          {errors.organizationName && <p className="text-red-500 text-sm">{errors.organizationName}</p>}
        </div>

        {/* Category + Research Area */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block mb-2 font-poppins font-bold text-white">Category*</label>
            <select
              className="w-full h-[45px] border border-gray-300 rounded-md px-3"
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
            >
              <option value="">Select Category</option>
              <option value="Institutions">Institutions</option>
              <option value="Industry Partners">Industry Partners</option>
              <option value="Funding Agencies">Funding Agencies</option>
              <option value="Government Organization">Government Organizations</option>
              <option value="NGO">NGO</option>
            </select>
          </div>

          <div>
            <label className="block mb-2 font-poppins font-bold text-white">Research Area*</label>
            <select
              className="w-full h-[45px] border border-gray-300 rounded-md px-3"
              name="researchArea"
              value={formData.researchArea}
              onChange={handleChange}
              required
            >
              <option value="">Select Research Area</option>
              <option value="Education">Education</option>
              <option value="HealthCare">HealthCare</option>
              <option value="Logistics">Logistics</option>
              <option value="Agriculture">Agriculture</option>
              <option value="AnimalHusbandry">Animal Husbandry</option>
              <option value="InformationTechnology">Information Technology</option>
              <option value="Insurance">Insurance</option>
            </select>
          </div>
        </div>

        {/* Start Date + End Date */}
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <DatePicker
              label="Start Date*"
              value={formData.startDate}
              onChange={(newValue) => handleChange({ target: { name: 'startDate', value: newValue } })}
              minDate={new Date('2017-01-01')}
              renderInput={(params) => (
                <TextField {...params} required className="w-full h-[45px]" />
              )}
            />
            {errors.startDate && <p className="text-red-500 text-sm">{errors.startDate}</p>}

            <DatePicker
              label="End Date*"
              value={formData.endDate}
              onChange={(newValue) => handleChange({ target: { name: 'endDate', value: newValue } })}
              minDate={new Date('2017-01-01')}
              renderInput={(params) => (
                <TextField {...params} required className="w-full h-[45px]" />
              )}
            />
            {errors.endDate && <p className="text-red-500 text-sm">{errors.endDate}</p>}
          </div>
        </LocalizationProvider>

        {/* Team Size + Members */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <TextFields
            label="Team Size*"
            name="teamSize"
            type="number"
            value={formData.teamSize}
            onChange={handleChange}
            placeholder="Enter Team Size"
            required
            className="w-full h-[45px]"
          />
          {errors.teamSize && <p className="text-red-500 text-sm">{errors.teamSize}</p>}

          <TextFields
            label="Team Members*"
            name="teamMembers"
            type="text"
            value={formData.teamMembers}
            onChange={handleChange}
            placeholder="Enter Team Members (comma separated)"
            required
            className="w-full h-[45px]"
          />
          {errors.teamMembers && <p className="text-red-500 text-sm">{errors.teamMembers}</p>}
        </div>

        {/* Project Description */}
        <div>
          <TextArea
            label="Project Description*"
            name="projectDescription"
            type="text"
            value={formData.projectDescription}
            onChange={handleChange}
            placeholder="Enter Project Description"
            required
            className="w-full h-[80px]"
          />
        </div>

        {/* Buttons */}
        <div className="flex justify-center mt-6">
          <button
            type="button"
            onClick={onClose}
            className="bg-black text-white py-2 px-6 rounded-lg mr-4"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSave}
            className="bg-black text-white py-2 px-6 rounded-lg"
          >
            Save
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddCollaborationForm;
